/**
 * Pied de page de la feuille journalière
 * Affiche les totaux et les boutons d'action
 */

import React, { useMemo } from 'react';
import { getUserInfoFromToken } from '../../utils/authUtils';

const WorksheetFooter = ({
  entries,
  onSave,
  onSubmit,
  onValidate,
  onInvalidate,
  onCheck,
  onClose,
  saving,
  isDirty,
  isEditable,
  isValidator,
  status,
  worksheetId
}) => {
  // Vérifier si l'utilisateur est chef d'équipe (masquer les compteurs)
  const isTeamLeader = useMemo(() => {
    const token = localStorage.getItem('token');
    const user = getUserInfoFromToken(token);
    const roleLower = (user?.role || '').toLowerCase();
    // Vérifier si le rôle commence par "chef" (seul rôle avec ce préfixe)
    return roleLower.startsWith('chef');
  }, []);

  // Seuls les rôles Comptabilité / Admin peuvent contrôler une feuille validée
  const isAccountant = useMemo(() => {
    const token = localStorage.getItem('token');
    const user = getUserInfoFromToken(token);
    return user?.role === 'Comptabilité' || user?.role === 'Admin';
  }, []);

  // Calculer les totaux (exclure les cadres et les absents des heures)
  const totals = entries.reduce((acc, entry) => {
    if (!entry.is_manager && !entry.is_absent) {
      acc.workHours += parseFloat(entry.work_hours || 0);
      acc.travelHours += parseFloat(entry.travel_hours || 0);
      acc.miscHours += parseFloat(entry.misc_hours || 0);
      acc.totalHours += parseFloat(entry.total_hours || 0);
    }
    if (!entry.is_absent) {
      acc.restaurantCount += entry.restaurant_lunch ? 1 : 0;
      acc.hotelNights += parseInt(entry.hotel_nights || 0);
    }
    return acc;
  }, {
    workHours: 0,
    travelHours: 0,
    miscHours: 0,
    totalHours: 0,
    restaurantCount: 0,
    hotelNights: 0,
    absentCount: 0,
    presentCount: 0
  });

  // Compter présents/absents
  totals.absentCount = entries.filter(e => e.is_absent).length;
  totals.presentCount = entries.length - totals.absentCount;

  const formatHours = (hours) => {
    return hours.toFixed(2) + 'h';
  };

  const canSubmit = worksheetId && !isDirty && (status === 'draft' || status === 'rejected');
  const canValidate = worksheetId && !isDirty && status === 'submitted' && isValidator;

  return (
    <div className="worksheet-footer">
      {/* Résumé des totaux - masqué pour les chefs d'équipe */}
      {!isTeamLeader && (
        <div className="totals-section">
          <h4>Totaux ({totals.presentCount} present{totals.presentCount > 1 ? 's' : ''}{totals.absentCount > 0 ? `, ${totals.absentCount} absent${totals.absentCount > 1 ? 's' : ''}` : ''})</h4>
          <div className="totals-grid">
            <div className="total-item">
              <span className="label">Heures travail</span>
              <span className="value">{formatHours(totals.workHours)}</span>
            </div>
            <div className="total-item">
              <span className="label">Heures route</span>
              <span className="value">{formatHours(totals.travelHours)}</span>
            </div>
            <div className="total-item">
              <span className="label">Divers</span>
              <span className="value">{formatHours(totals.miscHours)}</span>
            </div>
            <div className="total-item highlight">
              <span className="label">TOTAL</span>
              <span className="value">{formatHours(totals.totalHours)}</span>
            </div>
            <div className="total-item">
              <span className="label">Repas restaurant</span>
              <span className="value">{totals.restaurantCount}</span>
            </div>
            <div className="total-item">
              <span className="label">Nuits hôtel</span>
              <span className="value">{totals.hotelNights}</span>
            </div>
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="actions-section">
        {isDirty && (
          <span className="dirty-indicator">Modifications non sauvegardées</span>
        )}

        <div className="action-buttons">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Fermer
          </button>

          {isEditable && (
            <>
              <button
                className="btn btn-primary"
                onClick={onSave}
                disabled={saving || !isDirty}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>

              {/* Soumettre : brouillon ou rejeté (pas pour les validateurs sur une feuille soumise) */}
              {(status === 'draft' || status === 'rejected') && (
                <button
                  className="btn btn-success"
                  onClick={onSubmit}
                  disabled={saving || !canSubmit}
                  title={!canSubmit ? 'Enregistrez d\'abord les modifications' : 'Soumettre pour validation RH'}
                >
                  Soumettre pour validation
                </button>
              )}

              {/* Valider : feuille soumise, validateur, pas de modifications en cours */}
              {status === 'submitted' && isValidator && (
                <button
                  className="btn btn-success"
                  onClick={onValidate}
                  disabled={saving || !canValidate}
                  title={isDirty ? 'Enregistrez d\'abord les modifications' : 'Valider cette feuille'}
                >
                  Valider
                </button>
              )}
            </>
          )}

          {status === 'submitted' && !isValidator && (
            <span className="status-info">
              En attente de validation RH
            </span>
          )}

          {status === 'validated' && (
            <>
              <span className="status-info validated">
                Feuille validée
              </span>
              {/* Dévalider : uniquement pour les validateurs */}
              {isValidator && onInvalidate && (
                <button
                  className="btn btn-warning"
                  onClick={onInvalidate}
                  disabled={saving}
                  title="Rouvrir cette feuille pour modification"
                >
                  Dévalider
                </button>
              )}
              {/* Contrôler (comptabilité) : valide le contrôle des heures, action terminale */}
              {isAccountant && onCheck && (
                <button
                  className="btn btn-primary"
                  onClick={onCheck}
                  disabled={saving}
                  title="Marquer comme contrôlée (action définitive)"
                >
                  🔒 Contrôler
                </button>
              )}
            </>
          )}

          {status === 'checked' && (
            <span className="status-info checked">
              Feuille contrôlée (comptabilité)
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorksheetFooter;
